import { useState } from "react";
import { useNavigate } from "react-router-dom";

const SALES_PASSWORD_KEY = "smartered-sales-password";

/**
 * Verify the entered sales password SERVER-SIDE.
 *
 * H6 (docs/deep-analysis-2026-07-05.md): the password was previously hardcoded
 * in this file and compared client-side, leaking the credential in the public
 * JS bundle. There is no dedicated verify endpoint, and we want to avoid new
 * backend surface, so we reuse POST /api/orders/quote-link with a minimal
 * dry-run payload: the endpoint is stateless (it generates an HMAC link and
 * stores nothing), so a 200 proves the password and a 401 rejects it.
 * Request shape mirrors QuoteLinkModal, which calls the same endpoint.
 */
async function verifySalesPassword(password: string): Promise<{ ok: boolean; invalid: boolean }> {
  const response = await fetch(`/api/orders/quote-link?_t=${Date.now()}`, {
    method: "POST",
    cache: "no-store",
    credentials: "omit",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
    },
    body: JSON.stringify({
      school_id: "salesteam-login-check",
      school_name: "Sales login check",
      items: [{ product_id: "hsc_std1", quantity: 1 }],
      sales_password: password,
    }),
  });

  if (response.ok) {
    return { ok: true, invalid: false };
  }
  return { ok: false, invalid: response.status === 401 };
}

export function SalesTeamPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting || !password) return;

    setSubmitting(true);
    setError("");

    try {
      const result = await verifySalesPassword(password);

      if (result.ok) {
        sessionStorage.setItem(SALES_PASSWORD_KEY, password);
        navigate("/");
      } else if (result.invalid) {
        setError("Invalid password. Please try again.");
        setPassword("");
      } else {
        setError("Unable to verify password right now. Please try again later.");
      }
    } catch {
      setError("Unable to verify password right now. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="sales-team-page">
      <div className="sales-team-container">
        <h1>SmarterEd Sales Portal</h1>
        <p className="sales-team-description">
          Enter your sales password to access quote link generation.
        </p>

        <form onSubmit={handleSubmit} className="sales-team-form">
          <div className="form-group">
            <label htmlFor="password">Sales Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError("");
              }}
              placeholder="Enter password"
              autoFocus
            />
          </div>

          {error && <div className="sales-team-error">{error}</div>}

          <button type="submit" className="btn btn-primary btn-full" disabled={submitting}>
            {submitting ? "Verifying..." : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
}
